import { useState } from "react";
import { motion } from "framer-motion";
import { Gift, Minus, Plus, Mail, CalendarHeart, Sparkles } from "lucide-react";
import { useSite, useCart } from "../lib/store";
import { Dots } from "../components/Bits";
import { useSeo } from "../components/Seo";

const TINTS = ["var(--pink-soft)", "var(--teal-soft)", "var(--sun-soft)", "var(--lilac-soft)", "var(--powder)"];

function VoucherCard({ value, i }) {
  const { addVoucher } = useCart();
  const [qty, setQty] = useState(1);
  return (
    <motion.div initial={{ opacity: 0, y: 30, rotate: i % 2 ? 2 : -2 }} whileInView={{ opacity: 1, y: 0, rotate: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} whileHover={{ y: -6, rotate: i % 2 ? 1 : -1 }}
      className="relative rounded-[32px] bg-white shadow-[0_24px_50px_-28px_rgba(196,111,189,.6)] flex flex-col sm:flex-row overflow-hidden" data-testid={`voucher-card-${value}`}>
      <div className="relative sm:w-[42%] p-6 flex flex-col items-center justify-center text-center" style={{ background: TINTS[i % 5] }}>
        <Dots />
        <img src="/logo.png" alt="Materiály pro školky" className="relative h-24 sm:h-28 w-auto drop-shadow-sm" />
        <p className="relative font-display leading-tight mt-2 text-xl">Materiály<br /><span className="text-[var(--pink-dark)]">pro školky</span></p>
      </div>
      <div className="relative hidden sm:block w-0 border-l-2 border-dashed border-[var(--pink)] my-6">
        <span className="absolute -top-9 -left-[13px] w-6 h-6 rounded-full bg-[var(--powder)]" />
        <span className="absolute -bottom-9 -left-[13px] w-6 h-6 rounded-full bg-[var(--powder)]" />
      </div>
      <div className="relative flex-1 p-6 flex flex-col">
        <p className="font-display text-xs tracking-[0.3em] text-[var(--teal)]">DÁRKOVÝ POUKAZ</p>
        <p className="text-sm font-semibold text-[var(--muted)] mt-1">s láskou pro děti</p>
        <p className="font-display text-6xl text-[var(--pink-dark)] mt-3 leading-none" data-testid={`voucher-value-${value}`}>{value}<span className="text-2xl ml-1">Kč</span></p>
        <p className="text-sm text-[var(--muted)] mt-2">platnost 1 rok · jedinečný kód v PDF</p>
        <div className="flex items-center gap-3 mt-auto pt-6">
          <div className="flex items-center bg-[var(--blush)] rounded-full">
            <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-9 grid place-items-center" data-testid={`voucher-minus-${value}`} aria-label="Méně"><Minus className="w-4 h-4" /></button>
            <span className="w-6 text-center font-bold" data-testid={`voucher-qty-${value}`}>{qty}</span>
            <button onClick={() => setQty(qty + 1)} className="w-9 h-9 grid place-items-center" data-testid={`voucher-plus-${value}`} aria-label="Více"><Plus className="w-4 h-4" /></button>
          </div>
          <button onClick={() => addVoucher(value, qty)} className="btn-pink flex-1 !px-3" data-testid={`voucher-add-${value}`}><Gift className="w-4 h-4" /> Do košíku</button>
        </div>
      </div>
    </motion.div>
  );
}

export default function Vouchers() {
  const { site } = useSite();
  useSeo({ title: "Dárkové poukazy na materiály pro školky", description: site?.texts.vouchers_text });
  if (!site) return <div className="h-[60vh]" />;
  return (
    <div data-testid="vouchers-page">
      <section className="relative paper overflow-hidden">
        <Dots />
        <div className="max-w-5xl mx-auto px-6 py-16 text-center relative">
          <p className="eyebrow mb-3">Dárkové poukazy</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold">Darujte <span className="squiggle">radost z tvoření</span></h1>
          <p className="mt-5 text-[var(--muted)] max-w-2xl mx-auto">{site.texts.vouchers_text}</p>
          <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm font-semibold">
            <span className="flex items-center gap-2"><Mail className="w-4 h-4 text-[var(--teal)]" /> Poukaz přijde e-mailem v PDF</span>
            <span className="flex items-center gap-2"><CalendarHeart className="w-4 h-4 text-[var(--teal)]" /> Platnost 1 rok</span>
            <span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[var(--teal)]" /> Lze čerpat postupně</span>
          </div>
        </div>
      </section>
      <div className="bg-[var(--powder)]">
        <div className="max-w-6xl mx-auto px-6 py-14 grid md:grid-cols-2 gap-8">
          {site.voucher_values.map((v, i) => <VoucherCard key={v} value={v} i={i} />)}
        </div>
      </div>
    </div>
  );
}
